/** SUNTRONIX ONE — Office Deployment Batches Screen (Phase 1). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PackageCheck, RefreshCcw, ChevronRight, CheckCircle2 } from "lucide-react";

const STATUS_TONE = {
  DRAFT:              "bg-slate-100 text-slate-700",
  SUBMITTED:          "bg-sky-100 text-sky-800",
  ALLOCATED:          "bg-indigo-100 text-indigo-800",
  DISPATCHED:         "bg-amber-100 text-amber-900",
  DELIVERED:          "bg-emerald-100 text-emerald-800",
  RECEIPT_CONFIRMED:  "bg-emerald-200 text-emerald-900",
  PARTIALLY_ASSIGNED: "bg-fuchsia-100 text-fuchsia-800",
  FULLY_ASSIGNED:     "bg-slate-800 text-white",
  CANCELLED:          "bg-slate-200 text-slate-500 line-through",
};


export default function OfficeBatches() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [assets, setAssets] = useState([]);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/office-deployment/batches");
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (batch) => {
    setDetail(batch);
    setAssets([]);
    if (batch.asset_ids?.length) {
      try {
        const { data } = await api.post("/assets/lookup", { asset_ids: batch.asset_ids });
        setAssets(data.items || []);
      } catch {
        // Fallback: fetch each individually (limited scope)
        const results = [];
        for (const id of batch.asset_ids.slice(0, 30)) {
          try {
            const { data } = await api.get(`/assets/${id}`);
            results.push(data);
          } catch { /* ignore */ }
        }
        setAssets(results);
      }
    }
  };

  const confirmReceipt = async (batch, recipient) => {
    try {
      await api.post(`/office-deployment/batches/${batch.id}/confirm-receipt`, { recipient });
      toast.success("Receipt confirmed");
      setConfirming(null);
      load();
      if (detail?.id === batch.id) {
        const { data } = await api.get("/office-deployment/batches");
        const fresh = (data.items || []).find((x) => x.id === batch.id);
        if (fresh) openDetail(fresh);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="office-batches-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Operations · Office Deploy
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <PackageCheck className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Office Deployment Batches
          </h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Delivery batches to Customer locations without individual employee assignment.
            Drill in to review serials, confirm office receipt, and track later assignment progress.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}
          className="h-10" data-testid="obd-reload">
          <RefreshCcw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Reload
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Batch</th>
              <th className="text-left px-3 py-2">Target</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-right px-3 py-2">Counts</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500">
                {busy ? "Loading…" : "No office batches yet — create one from Operations → Deploy → Customer Office."}
              </td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} data-testid={`obd-row-${b.code}`} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="font-mono text-[13px] text-slate-900">{b.code}</div>
                  <div className="text-[11px] text-slate-500">{(b.created_at || "").slice(0, 10)}</div>
                </td>
                <td className="px-3 py-2 text-[12px]">
                  {(b.deployment_target || "—").replace(/_/g, " ")}
                  {b.deployment_purpose && (
                    <div className="text-[10px] text-slate-500">{b.deployment_purpose.replace(/_/g, " ")}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] font-mono text-slate-700">
                  {b.customer_location_id || "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="text-[12px] font-mono">
                    <span className="text-emerald-700">{b.counts?.available ?? "—"}</span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-amber-700">{b.counts?.reserved ?? "—"}</span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-fuchsia-700">{b.counts?.shared ?? "—"}</span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-sky-700">{b.counts?.assigned ?? "—"}</span>
                  </div>
                  <div className="text-[10px] text-slate-400">avail · reserved · shared · assigned</div>
                </td>
                <td className="px-3 py-2">
                  <span className={"h-5 px-2 rounded-full text-[10px] font-medium uppercase tracking-[0.1em] " +
                    (STATUS_TONE[b.status] || "bg-slate-100 text-slate-700")}>
                    {(b.status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1.5">
                    {(b.status === "DISPATCHED" || b.status === "DELIVERED" || b.status === "DRAFT") && (
                      <Button size="sm" variant="outline" className="h-8"
                        onClick={() => setConfirming(b)}
                        data-testid={`obd-confirm-${b.code}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Confirm receipt
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8"
                      onClick={() => openDetail(b)}
                      data-testid={`obd-open-${b.code}`}>
                      Open <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <Dialog open onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl" data-testid="obd-detail-dialog">
            <DialogHeader><DialogTitle>{detail.code} · Batch detail</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2 text-sm">
              <Field label="Target">{(detail.deployment_target || "").replace(/_/g, " ")}</Field>
              <Field label="Purpose">{(detail.deployment_purpose || "—").replace(/_/g, " ")}</Field>
              <Field label="Location ID"><span className="font-mono text-[12px]">{detail.customer_location_id || "—"}</span></Field>
              <Field label="Status">{(detail.status || "").replace(/_/g, " ")}</Field>
              <Field label="Receiving contact">
                {detail.receiving_contact?.name || "—"} · {detail.receiving_contact?.phone || ""}
              </Field>
              <Field label="Custodian">
                {detail.custodian?.name || "—"} · {detail.custodian?.email || ""}
              </Field>
            </div>
            <div className="border-t border-slate-200 pt-2">
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-2">
                Assets in batch ({detail.asset_ids?.length || 0})
              </div>
              <div className="max-h-64 overflow-auto border border-slate-200 rounded-md">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Serial</th>
                      <th className="text-left px-2 py-1.5">Tag</th>
                      <th className="text-left px-2 py-1.5">Model</th>
                      <th className="text-left px-2 py-1.5">Custody</th>
                      <th className="text-left px-2 py-1.5">Assignee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {assets.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-slate-500">
                        {detail.asset_ids?.length ? "Loading assets…" : "No assets in this batch."}
                      </td></tr>
                    ) : assets.map((a) => (
                      <tr key={a.id}>
                        <td className="px-2 py-1.5 font-mono">{a.serial || "—"}</td>
                        <td className="px-2 py-1.5">{a.asset_tag || "—"}</td>
                        <td className="px-2 py-1.5">{a.brand} {a.model}</td>
                        <td className="px-2 py-1.5 text-[11px]">{(a.custody_state || "—").replace(/_/g, " ")}</td>
                        <td className="px-2 py-1.5">{a.assigned_employee_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirming && (
        <ConfirmDialog batch={confirming} onCancel={() => setConfirming(null)}
          onSave={(recipient) => confirmReceipt(confirming, recipient)} />
      )}
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="text-slate-800 mt-1">{children}</div>
    </div>
  );
}


function ConfirmDialog({ batch, onCancel, onSave }) {
  const [recipient, setRecipient] = useState(batch?.receiving_contact?.name || "");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="obd-confirm-dialog">
        <DialogHeader><DialogTitle>Confirm office receipt · {batch.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-[12px] text-slate-600">
            Assets in this batch: <strong>{batch.asset_ids?.length || 0}</strong>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Recipient at location *</div>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder="Security Desk / IT Admin / etc."
              data-testid="obd-confirm-recipient" />
          </div>
          <div className="text-[11px] text-slate-500">
            On confirmation, every asset in this batch flips to <strong>CUSTOMER_LOCATION_CUSTODY</strong>.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(recipient)} disabled={!recipient}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="obd-confirm-save">Confirm receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
